import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Select, Label, Field } from '@shared/ui/Input';
import { ACT_FORM_CODES, formLabel } from '@shared/lib/smetaLabels';
import type { KsPackage, KsPackageAct, KsPackageForm } from '@shared/api';

const TableTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-weight: 600;
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Err = styled.div`
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.status.danger};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 0.85rem;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 0.85rem;
`;

const Hint = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 0.8rem;
  line-height: 1.4;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Inline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: middle;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const SpacedErr = styled(Err)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const SpacedMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const FormsInline = styled(Inline)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const ActInline = styled(Inline)`
  margin-top: ${({ theme }) => theme.spacing(2.5)};
`;

const ActField = styled(Field)`
  margin-bottom: 0;
`;

const ActSelect = styled(Select)`
  min-width: 140px;
`;

const FormCodeSelect = styled(Select)`
  min-width: 240px;
`;

export interface KsPackageFormsTabProps {
  pkg: KsPackage;
  forms: KsPackageForm[];
  formsError?: unknown;
  formsLoading?: boolean;
  refreshingForms: boolean;
  onRefreshForms: () => void;
  onRender: (forms: string[]) => void;
  onExport: () => void;
  rendering: boolean;
  exporting: boolean;
  canWrite: boolean;
  frozen: boolean;
  busy: boolean;
  renderQueuedAt: number;
  renderWatchUntil: number;
  acts: KsPackageAct[];
  formActId: string;
  setFormActId: (id: string) => void;
  formCode: string;
  setFormCode: (code: string) => void;
  renderingAct: boolean;
  onRenderAct: (actId: string, actNo: string, forms: string[]) => void;
  onDownloadForm: (formId: string, filename: string) => void;
  apiErr: (err: unknown, fallback: string) => string;
}

export function KsPackageFormsTab({
  pkg,
  forms,
  formsError,
  formsLoading,
  refreshingForms,
  onRefreshForms,
  onRender,
  onExport,
  rendering,
  exporting,
  canWrite,
  frozen,
  busy,
  renderQueuedAt,
  renderWatchUntil,
  acts,
  formActId,
  setFormActId,
  formCode,
  setFormCode,
  renderingAct,
  onRenderAct,
  onDownloadForm,
  apiErr,
}: KsPackageFormsTabProps) {
  return (
    <>
      {/* Выпуск — очередью: КС-6а это тысячи строк на десятки
          колонок, синхронный рендер занял бы единственный воркер
          doc-processor на всё это время. */}
      <TableTitle>
        Печатные формы <Badge $tone="neutral">{forms.length}</Badge>
        {pkg.status === 'rendering' && <Badge $tone="warning">выпускаются…</Badge>}
        {/* Автоожидание форм конечно (RENDER_WATCH_MS), а очередь
            воркера — общая: без ручного обновления единственным
            выходом из «поставлено в очередь, а таблица пуста»
            остаётся F5. */}
        <Button
          $variant="ghost"
          $size="sm"
          type="button"
          disabled={refreshingForms}
          title="Перечитать список выпущенных форм"
          onClick={onRefreshForms}
        >
          {refreshingForms ? 'Обновляю…' : 'Обновить'}
        </Button>
      </TableTitle>
      {/* Отказ чтения списка форм выглядел бы как «форм нет» —
          то есть как «выпуск не сработал». Это разные новости: во
          втором случае формы могут лежать на сервере готовыми. */}
      {Boolean(formsError) && (
        <SpacedErr>
          Список форм не прочитан: {apiErr(formsError, 'сервер не ответил')}. Что
          выпущено на самом деле — сейчас неизвестно; нажмите «Обновить».
        </SpacedErr>
      )}
      {/* «Форм нет» тоже значит разное, и обещать «файлы появятся
          позже» можно только когда выпуск и правда поставлен в
          очередь. Иначе сметчик ждёт результат задания, которого
          никто не запускал, — либо упавшего. */}
      {forms.length === 0 && !formsError && !formsLoading && (
        <SpacedMuted>
          {pkg.status === 'rendering'
            ? 'Формы выпускаются — статус пакета «рендер форм…». Список оживёт сам.'
            : renderQueuedAt === 0
              ? 'Выпущенных форм нет, и в этой сессии выпуск не запускали. Нажмите ' +
                '«Выпустить комплект» ниже — либо «Комплект акта» на шаге «Договор ' +
                'и период», если нужен один акт со спутниками.'
              : renderWatchUntil > 0
                ? 'Выпуск поставлен в очередь. Воркер doc-processor один на всех: ' +
                  'если он занят чужим заданием, файлы появятся позже — список ' +
                  'обновляется сам, пока идёт ожидание.'
                : 'Выпуск поставлен в очередь больше минуты назад, а форм всё ещё ' +
                  'нет: либо очередь длинная, либо задание упало. Нажмите ' +
                  '«Обновить»; упавший рендер переводит пакет в «ошибка сборки» и ' +
                  'пишет причину в шапке пакета.'}
        </SpacedMuted>
      )}
      <FormsInline>
        <Button
          $variant="primary"
          $size="sm"
          type="button"
          onClick={() => onRender([])}
          disabled={rendering || !canWrite || frozen || busy}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Выпустить комплект
        </Button>
        <Button
          $variant="primary"
          $size="sm"
          type="button"
          onClick={onExport}
          disabled={exporting || forms.length === 0}
          title={
            forms.length === 0
              ? 'Комплект ещё не выпущен'
              : 'Все формы комплекта одним архивом'
          }
        >
          {exporting ? 'Собираю архив…' : 'Выгрузить комплект (ZIP)'}
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onRender(['ks6a'])}
          disabled={rendering || !canWrite || frozen || busy}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Выпустить КС-6а
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onRender(['registry_ks2'])}
          disabled={rendering || !canWrite || frozen || busy}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Выпустить реестр актов
        </Button>
        <Button
          $variant="secondary"
          $size="sm"
          type="button"
          onClick={() => onRender(['registry_ks2_b'])}
          disabled={rendering || !canWrite || frozen || busy}
          title={!canWrite ? 'Только чтение' : undefined}
        >
          Реестр с корзинами затрат
        </Button>
      </FormsInline>
      <Hint>
        В архив попадают формы, включённые ПРОФИЛЕМ ДОГОВОРА, по последней редакции
        каждой. КС-6а и оба реестра — формы уровня договора: журнал печатает историю по
        выпущенным пакетам, реестр перечисляет акты (в мире B — все сметы договора,
        включая незакрытые). Расхождения сверок приходят находками, а не молча.
      </Hint>

      {/* Отдельный бланк-спутник по конкретному акту. Акт выбирается
          явно: приложения выпускаются ПО АКТУ, и «выпустить ВВМП»
          без указания акта означало бы перевыпустить их все —
          включая уже отправленные заказчику. */}
      {acts.length > 0 && (
        <ActInline>
          <ActField>
            <Label>Акт</Label>
            <ActSelect
              value={formActId}
              onChange={(e) => setFormActId(e.target.value)}
            >
              {acts.map((a) => (
                <option key={a.id} value={a.id}>
                  № {a.act_no}
                </option>
              ))}
            </ActSelect>
          </ActField>
          <ActField>
            <Label>Бланк акта</Label>
            <FormCodeSelect
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
            >
              {ACT_FORM_CODES.map((c) => (
                <option key={c} value={c}>
                  {formLabel(c)}
                </option>
              ))}
            </FormCodeSelect>
          </ActField>
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={
              rendering || renderingAct || !canWrite || frozen || busy || !formActId
            }
            title={!canWrite ? 'Только чтение' : undefined}
            onClick={() => {
              const act = acts.find((a) => a.id === formActId);
              if (act) onRenderAct(act.id, act.act_no, [formCode]);
            }}
          >
            Выпустить бланк
          </Button>
        </ActInline>
      )}
      <Hint>
        Приложения выпускаются по акту и только когда у него есть основание: свои
        материалы — ВВМП (мир A) либо Приложение №1 (мир B), давальческие — ВВДМ, М-19
        и М-29, оборудование Заказчика — ВСО, калибровка компенсации — ФОТ, рейсы —
        «Доставка». Если основание есть, а бланка нет в профиле договора, сборка
        поднимает находку «нет формы в комплекте» — она блокирует выпуск, чтобы
        неполный комплект не уехал заказчику.
      </Hint>
      {forms.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Форма</th>
                <th>Файл</th>
                <th>Пересборка</th>
                <th>Расхождения</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {forms.map((f) => (
                <tr key={f.id}>
                  <td>{formLabel(f.form_code)}</td>
                  <td>{f.filename}</td>
                  <td>№ {f.rebuild_no}</td>
                  <td>
                    {f.warnings.length === 0 ? (
                      <Badge $tone="success">сходится</Badge>
                    ) : (
                      <Badge
                        $tone="warning"
                        title={f.warnings.map((w) => w.message).join('; ')}
                      >
                        {f.warnings.length}
                      </Badge>
                    )}
                  </td>
                  <td>
                    <Button
                      $variant="ghost"
                      $size="sm"
                      type="button"
                      onClick={() => onDownloadForm(f.id, f.filename)}
                    >
                      Скачать
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </>
  );
}
